import React from 'react';
import {
  PhoneIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { formatDuracionLegible } from './callHistoryTime.util';

/**
 * Tarjetas KPI de Resumen Operacional (Modo Claro)
 * Estilizadas según especificación Stitch y diseño institucional Credipoint.
 */
export function CallHistorySummaryCards({ resumen, loading }) {
  if (!resumen && loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-4 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-28 bg-white rounded-xl border border-slate-200 shadow-xs p-4 flex flex-col justify-between"
          >
            <div className="h-4 bg-slate-100 rounded w-1/2" />
            <div className="h-7 bg-slate-100 rounded w-1/3" />
            <div className="h-3 bg-slate-100 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (!resumen) return null;

  const total = Number(resumen.totalLlamadas) || 0;
  const contestadas = Number(resumen.contestadas) || 0;
  const tasaEfectividad = total > 0 ? Math.round((contestadas / total) * 100) : 0;
  const salientes = Number(resumen.salientes) || 0;
  const entrantes = Number(resumen.entrantes) || 0;
  const sinRespuesta = Number(resumen.sinRespuesta) || 0;
  const ocupado = Number(resumen.ocupado) || 0;

  // Cálculo de progreso para indicador circular de cuota (meta diaria estimada: 2h = 120 min)
  const totalMinutosEstimados = (() => {
    const raw = resumen.tiempoHabladoTotalFormatted || '';
    const match = raw.match(/(\d+)\s*m/);
    return match ? parseInt(match[1], 10) : 0;
  })();
  const metaMinutos = 120; // 2 horas
  const porcentajeMeta = Math.min(100, Math.max(10, Math.round((totalMinutosEstimados / metaMinutos) * 100)));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-4">
      {/* 1. Total Llamadas */}
      <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-xs flex flex-col justify-between hover:shadow-sm hover:border-slate-300 transition-all">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Total Llamadas
            </span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 tracking-tight">
              {total}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shadow-xs">
            <PhoneIcon className="w-5 h-5" />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between pt-2.5 border-t border-slate-100 text-xs">
          <span className="text-slate-500 font-medium text-[11px]">
            {salientes} salientes, {entrantes} entrantes
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200/70">
            <ArrowTrendingUpIcon className="w-3 h-3 text-emerald-600" />
            Operativas
          </span>
        </div>
      </div>

      {/* 2. Contestadas / Efectividad */}
      <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-xs flex flex-col justify-between hover:shadow-sm hover:border-slate-300 transition-all">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
              Contestadas (Efectividad)
            </span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 tracking-tight flex items-baseline gap-2">
              <span>{contestadas}</span>
              <span className="text-base sm:text-lg font-bold text-emerald-600">
                ({tasaEfectividad}%)
              </span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center shadow-xs">
            <CheckCircleIcon className="w-5 h-5" />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1.5 pt-2.5 border-t border-slate-100 text-[11px]">
          <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200/70 font-medium">
            {sinRespuesta} no contestó
          </span>
          <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200/70 font-medium">
            {ocupado} ocupado
          </span>
        </div>
      </div>

      {/* 3. Tiempo Hablado Total */}
      <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-xs flex flex-col justify-between hover:shadow-sm hover:border-slate-300 transition-all">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
              Tiempo Hablado Total
            </span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 tracking-tight">
              {resumen.tiempoHabladoTotalFormatted || '0m'}
            </div>
          </div>
          <div className="relative w-10 h-10 flex items-center justify-center">
            <svg className="w-10 h-10 transform -rotate-90">
              <circle
                className="text-slate-100"
                cx="20"
                cy="20"
                fill="transparent"
                r="16"
                stroke="currentColor"
                strokeWidth="3.5"
              />
              <circle
                className="text-blue-600 transition-all duration-500"
                cx="20"
                cy="20"
                fill="transparent"
                r="16"
                stroke="currentColor"
                strokeDasharray="100"
                strokeDashoffset={100 - porcentajeMeta}
                strokeWidth="3.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute text-[10px] font-black text-blue-700">
              {porcentajeMeta}%
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between pt-2.5 border-t border-slate-100 text-xs">
          <span className="text-slate-500 text-[11px] font-medium">
            Cuota estimada: 2h 00m
          </span>
        </div>
      </div>

      {/* 4. Promedio Hablado */}
      <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-xs flex flex-col justify-between hover:shadow-sm hover:border-slate-300 transition-all">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">
              Promedio Hablado
            </span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 tracking-tight">
              {formatDuracionLegible(resumen.tiempoHabladoPromedioSeg, resumen.tiempoHabladoPromedioFormatted)}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center shadow-xs">
            <ClockIcon className="w-5 h-5" />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between pt-2.5 border-t border-slate-100 text-xs">
          <span className="text-slate-500 text-[11px] font-medium">
            Por llamada efectiva
          </span>
          <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200/70">
            Benchmark: 03:45
          </span>
        </div>
      </div>
    </div>
  );
}
